import { zodResolver } from '@hookform/resolvers/zod';
import { KNOWN_AGENCIES, agencySchema, type Agency } from '@pgs/shared';
import { useForm } from 'react-hook-form';
import { focusFirstError } from '../focusFirstError';
import { NavButtons } from '../NavButtons';
import { StepPanel } from '../StepPanel';
import { NameInput } from '@/components/form/NameInput';
import { SelectField } from '@/components/form/SelectField';
import { TextField } from '@/components/form/TextField';

interface Props {
  defaultValues?: Partial<Agency>;
  onBack: () => void;
  onSubmit: (values: Agency) => void;
}

const AGENCY_OPTIONS = [
  ...KNOWN_AGENCIES.map((n) => ({ value: n, label: n })),
  { value: '__other__', label: 'Other / Not listed' },
];

export const Step2Agency = ({ defaultValues, onBack, onSubmit }: Props) => {
  const form = useForm<Agency & { _picker: string }>({
    resolver: zodResolver(agencySchema),
    defaultValues: { ...defaultValues, _picker: defaultValues?.name ?? '' } as never,
    mode: 'onBlur',
  });
  const {
    register,
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = form;

  const picker = watch('_picker');

  return (
    <StepPanel
      icon="fa-home"
      title="Your Agency"
      description={
        <>
          <strong className="text-teal">This is important.</strong>
          <br />
          <br />
          Please make sure this is filled in correctly.
        </>
      }
    >
      <form
        onSubmit={handleSubmit((v) => {
          // Drop the helper field before delegating upward.
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { _picker, ...rest } = v as Agency & { _picker: string };
          onSubmit(rest as Agency);
        }, focusFirstError)}
        className="flex flex-col"
      >
        <SelectField
          label="Please Select Your Agency Here"
          {...register('_picker' as never, {
            onChange: (e) => {
              const v = e.target.value;
              if (v && v !== '__other__') setValue('name', v);
              else if (v === '__other__') setValue('name', '');
            },
          })}
          placeholder="Select your agency..."
          options={AGENCY_OPTIONS}
        />
        {picker === '__other__' && (
          <TextField
            label="Enter Agency Name"
            placeholder="Type agency name manually"
            {...register('name')}
            error={errors.name?.message}
          />
        )}
        <TextField label="Branch" {...register('branch')} />
        <div className="mt-4 border-t border-ink-100 pt-4">
          <p className="mb-3 text-[11px] font-extrabold uppercase tracking-wider text-ink-900">
            Your Consultant
          </p>
          <NameInput control={control} name="consultantName" label="Consultant Name" />
          <TextField
            label="Consultant Email"
            type="email"
            {...register('consultantEmail')}
            error={errors.consultantEmail?.message}
          />
          <TextField
            label="Consultant Mobile (optional)"
            placeholder="07123456789"
            maxLength={11}
            inputMode="numeric"
            {...register('consultantMobile')}
            error={errors.consultantMobile?.message}
          />
        </div>
        <NavButtons onBack={onBack} submit />
      </form>
    </StepPanel>
  );
};
